
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  const [selectedFeature, setSelectedFeature] = useState('kundenverwaltung');
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    unternehmen: '',
    nachricht: ''
  });
  const [kontaktFormData, setKontaktFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    betreff: '',
    nachricht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const features = [
    {
      id: 'kundenverwaltung',
      title: 'Kundenverwaltung',
      icon: 'ri-user-line',
      description: 'Verwalten Sie alle Kundendaten zentral und übersichtlich'
    },
    {
      id: 'auftragsverwaltung',
      title: 'Auftragsverwaltung',
      icon: 'ri-file-list-line',
      description: 'Komplette Abwicklung von der Anfrage bis zur Rechnung'
    },
    {
      id: 'artikelverwaltung',
      title: 'Artikelverwaltung',
      icon: 'ri-box-line',
      description: 'Artikel und Materialien effizient verwalten'
    },
    {
      id: 'lv-system',
      title: 'LV-System',
      icon: 'ri-file-text-line',
      description: 'Ausschreibung, Angebot, Lieferschein, Rechnung - alles in einem'
    },
    {
      id: 'zahlungsueberwachung',
      title: 'Zahlungsüberwachung',
      icon: 'ri-money-euro-circle-line',
      description: 'Mahnwesen und Zahlungsüberwachung automatisiert'
    },
    {
      id: 'aufmass',
      title: 'Aufmaß-System',
      icon: 'ri-ruler-line',
      description: 'Basis- und erweiterte Aufmaß-Funktionen'
    }
  ];

  const additionalFeatures = [
    'Vorkalkulation & Nachkalkulation',
    'E-Mail Versand von Dokumenten',
    'XRechnung',
    'Bauzeit Pro',
    'Preisspiegel',
    'Tagesberichte',
    'Lagerverwaltung',
    'Jahresverträge',
    'Lieferscheinaufstellung',
    'Mandanten-Installation',
    'Brieftext/Rundschreiben',
    'Adressetiketten-Druck',
    'Datensicherung & Übertragung'
  ];

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      Object.entries(demoFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3osfnrfiitv34rgaorg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setSubmitMessage('Vielen Dank! Ihre Demo-Anfrage wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.');
        setDemoFormData({ name: '', email: '', telefon: '', unternehmen: '', nachricht: '' });
      } else {
        setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKontaktSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      Object.entries(kontaktFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3osfnrfiitv34rgaos0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setSubmitMessage('Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.');
        setKontaktFormData({ name: '', email: '', telefon: '', betreff: '', nachricht: '' });
      } else {
        setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-lightBlue to-primary-100 py-20 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/6c7296e0f21019d4097a777fbf00b548.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Die bewährte <span className="text-primary-600">Software</span> für Handwerker
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Seit 35 Jahren vertrauen über 15.000 Handwerker in Deutschland auf Baufaktura. 
                Verwalten Sie Kunden, Aufträge und Rechnungen professionell und effizient.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button size="lg" className="text-lg px-8 py-4">
                  <i className="ri-play-circle-line mr-2"></i>
                  Kostenlose Demo
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                  <i className="ri-download-line mr-2"></i>
                  Jetzt testen
                </Button>
              </div>
              <div className="flex items-center space-x-8 text-sm text-gray-600">
                <div className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                  35 Jahre Erfahrung
                </div>
                <div className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                  15.000+ Kunden
                </div>
                <div className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                  Made in Germany
                </div>
              </div>
            </div>
            <div className="w-full flex justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20construction%20management%20software%20interface%20on%20modern%20computer%20screen%2C%20clean%20dashboard%20with%20charts%20and%20project%20data%2C%20handworker%20tools%20and%20blueprints%20on%20desk%2C%20bright%20office%20environment%2C%20high-tech%20workspace%2C%20professional%20software%20design&width=600&height=500&seq=hero-software&orientation=landscape"
                alt="Baufaktura Software Interface"
                className="w-full max-w-lg rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funktionen" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Alle Funktionen für Ihren Handwerksbetrieb
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Von der Kundenverwaltung bis zur Rechnung - Baufaktura bietet alle Tools, 
              die Sie für die erfolgreiche Führung Ihres Handwerksbetriebs benötigen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {features.map((feature) => (
              <div
                key={feature.id}
                className={`p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                  selectedFeature === feature.id
                    ? 'border-primary-500 bg-lightBlue shadow-lg'
                    : 'border-lightGrey bg-white hover:border-primary-300 hover:shadow-md'
                }`}
                onClick={() => setSelectedFeature(feature.id)}
              >
                <div className="w-16 h-16 bg-lightBlue rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Additional Features */}
          <div className="bg-lightGrey rounded-3xl p-8 lg:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Weitere professionelle Funktionen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                  <i className="ri-check-circle-fill text-green-500 text-lg"></i>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="vorteile" className="py-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Warum über 15.000 Handwerker Baufaktura vertrauen
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Seit 35 Jahren entwickeln wir Software speziell für die Bedürfnisse 
              deutscher Handwerksbetriebe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">35 Jahre</h3>
              <p className="text-primary-100">Erfahrung in der Entwicklung von Handwerker-Software</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-heart-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">15.000+</h3>
              <p className="text-primary-100">Zufriedene Kunden in ganz Deutschland</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">100%</h3>
              <p className="text-primary-100">Made in Germany - Sicherheit und Qualität</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-headphone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">24/7</h3>
              <p className="text-primary-100">Support und umfassende Hilfe-Dokumentation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Software Versions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Flexible Lösungen für jeden Betrieb
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wählen Sie zwischen der klassischen Windows-Installation oder 
              unserer modernen Remote-Version.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-lightGrey to-lightGrey/80 rounded-3xl p-8 lg:p-12">
              <div className="w-16 h-16 bg-lightBlue rounded-xl flex items-center justify-center mb-6">
                <i className="ri-computer-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Windows Version</h3>
              <p className="text-gray-600 mb-6">
                Die bewährte Desktop-Version für Windows-Computer. Vollständige Kontrolle 
                über Ihre Daten und maximale Performance.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Lokale Datenspeicherung</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Offline-Funktionalität</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Maximale Performance</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Einmalige Lizenzkosten</span>
                </li>
              </ul>
              <Button variant="outline" fullWidth>
                Windows Version testen
              </Button>
            </div>

            <div className="bg-gradient-to-br from-lightBlue to-primary-100 rounded-3xl p-8 lg:p-12 border-2 border-primary-200">
              <div className="w-16 h-16 bg-lightBlue rounded-xl flex items-center justify-center mb-6">
                <i className="ri-cloud-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Remote Version</h3>
              <p className="text-gray-600 mb-6">
                Moderne Cloud-Lösung für flexibles Arbeiten von überall. 
                Automatische Updates und Backups inklusive.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Von überall zugänglich</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Automatische Backups</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Immer aktuell</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-circle-fill text-green-500 mr-3"></i>
                  <span>Monatliche Abrechnung</span>
                </li>
              </ul>
              <Button fullWidth>
                Remote Version testen
              </Button>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Möchten Sie die Unterschiede zwischen ECO und PREMIUM Version vergleichen?
            </p>
            <Button variant="outline" size="lg">
              <i className="ri-compare-line mr-2"></i>
              <Link to="/versionsvergleich" className="text-inherit no-underline">
                Detaillierter Versionsvergleich
              </Link>
            </Button>
          </div>

        </div>
      </section>

      {/* Demo Request Section */}
      <section id="preise" className="py-20 bg-lightGrey">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Kostenlose Demo anfordern
            </h2>
            <p className="text-xl text-gray-600">
              Überzeugen Sie sich selbst von den Vorteilen der Baufaktura Software. 
              Wir zeigen Ihnen gerne alle Funktionen in einer persönlichen Demo.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
            <form onSubmit={handleDemoSubmit} data-readdy-form id="demo-anfrage">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={demoFormData.name}
                    onChange={(e) => setDemoFormData({...demoFormData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={demoFormData.email}
                    onChange={(e) => setDemoFormData({...demoFormData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={demoFormData.telefon}
                    onChange={(e) => setDemoFormData({...demoFormData, telefon: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="+49 123 456 789"
                  />
                </div>
                <div>
                  <label htmlFor="unternehmen" className="block text-sm font-medium text-gray-700 mb-2">
                    Unternehmen
                  </label>
                  <input
                    type="text"
                    id="unternehmen"
                    name="unternehmen"
                    value={demoFormData.unternehmen}
                    onChange={(e) => setDemoFormData({...demoFormData, unternehmen: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Ihr Unternehmen"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="nachricht" className="block text-sm font-medium text-gray-700 mb-2">
                  Nachricht
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  rows={4}
                  maxLength={500}
                  value={demoFormData.nachricht}
                  onChange={(e) => setDemoFormData({...demoFormData, nachricht: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="Teilen Sie uns mit, welche Funktionen Sie besonders interessieren..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {demoFormData.nachricht.length}/500 Zeichen
                </div>
              </div>
              <Button 
                type="submit" 
                fullWidth 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Demo anfordern
                  </>
                )}
              </Button>
              {submitMessage && (
                <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('erfolgreich') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Umfassender Support für Ihren Erfolg
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir lassen Sie nicht allein. Profitieren Sie von unserem erfahrenen Support-Team 
              und umfangreichen Hilfe-Ressourcen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-lightBlue rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Persönlicher Support</h3>
              <p className="text-gray-600 mb-6">
                Unser erfahrenes Support-Team hilft Ihnen bei allen Fragen rund um Baufaktura.
              </p>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center justify-center">
                  <i className="ri-phone-line mr-2"></i>
                  <span>+49 (0) 123 456 789</span>
                </div>
                <div className="flex items-center justify-center">
                  <i className="ri-time-line mr-2"></i>
                  <span>Mo-Fr: 8:00-17:00 Uhr</span>
                </div>
              </div>
            </div>

            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-lightBlue rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-open-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Umfassendes Handbuch</h3>
              <p className="text-gray-600 mb-6">
                Detaillierte Anleitungen und Tutorials für alle Funktionen von Baufaktura.
              </p>
              <Button variant="outline" size="sm">
                <i className="ri-external-link-line mr-2"></i>
                help.baufaktura.de
              </Button>
            </div>

            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-lightBlue rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-graduation-cap-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Schulungen</h3>
              <p className="text-gray-600 mb-6">
                Persönliche Schulungen und Webinare für den optimalen Einstieg in Baufaktura.
              </p>
              <Button variant="outline" size="sm">
                <i className="ri-calendar-line mr-2"></i>
                Termin vereinbaren
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 bg-gradient-to-br from-primary-800 to-primary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Kontaktieren Sie uns
            </h2>
            <p className="text-xl text-primary-200">
              Haben Sie Fragen zu Baufaktura? Wir sind für Sie da und beraten Sie gerne.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 text-gray-900">
            <form onSubmit={handleKontaktSubmit} data-readdy-form id="kontakt">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="kontakt-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="kontakt-name"
                    name="name"
                    required
                    value={kontaktFormData.name}
                    onChange={(e) => setKontaktFormData({...kontaktFormData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label htmlFor="kontakt-email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="kontakt-email"
                    name="email"
                    required
                    value={kontaktFormData.email}
                    onChange={(e) => setKontaktFormData({...kontaktFormData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="kontakt-telefon" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="kontakt-telefon"
                    name="telefon"
                    value={kontaktFormData.telefon}
                    onChange={(e) => setKontaktFormData({...kontaktFormData, telefon: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="+49 123 456 789"
                  />
                </div>
                <div>
                  <label htmlFor="betreff" className="block text-sm font-medium text-gray-700 mb-2">
                    Betreff
                  </label>
                  <input
                    type="text"
                    id="betreff"
                    name="betreff"
                    value={kontaktFormData.betreff}
                    onChange={(e) => setKontaktFormData({...kontaktFormData, betreff: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Worum geht es?"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="kontakt-nachricht" className="block text-sm font-medium text-gray-700 mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="kontakt-nachricht"
                  name="nachricht"
                  rows={5}
                  maxLength={500}
                  required
                  value={kontaktFormData.nachricht}
                  onChange={(e) => setKontaktFormData({...kontaktFormData, nachricht: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="Ihre Nachricht an uns..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {kontaktFormData.nachricht.length}/500 Zeichen
                </div>
              </div>
              <Button 
                type="submit" 
                fullWidth 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Nachricht senden
                  </>
                )}
              </Button>
              {submitMessage && (
                <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('erfolgreich') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
